import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getInboxClient } from '@repletoryreach/sdk-realtime';
import type { TemplateEvents } from '@repletoryreach/sdk-realtime';
import { useRealtimeStore } from '@repletoryreach/sdk-utils/store';

/**
 * Typed subscription to the template-lifecycle events.
 *
 * They ride the inbox hub (one connection per tenant). The library's
 * `useInboxEvent` is typed only against `InboxEvents`, so this goes through
 * `getInboxClient().on()`, which accepts the union `InboxEvents &
 * TemplateEvents`. Ported from `apps/admin-ui/src/data/useTemplateEvent.ts`,
 * with the handler held in a ref for the same reason `useInboxEvent` now does.
 */
export function useTemplateEvent<E extends keyof TemplateEvents & string>(
  event: E,
  handler: (payload: TemplateEvents[E]) => void
): void {
  const status = useRealtimeStore((s) => s.status);
  const saved = useRef(handler);
  saved.current = handler;

  useEffect(() => {
    const client = getInboxClient();
    if (!client) return;
    return client.on(event as never, ((payload: TemplateEvents[E]) => saved.current(payload)) as never);
  }, [event, status]);
}

/**
 * Keeps the template queries in step with Meta's decisions.
 *
 * `templates.md` §4 item 4: `services/channel-wa` writes `template.approved`
 * and `template.rejected` to the outbox, `services/inbox` fans them out over
 * `/hubs/inbox`, and `sdk-realtime` types them: a complete, working pipeline
 * that the tenant console did not subscribe to. A template approved by Meta
 * stayed "submitted" on screen until the user reloaded the page by hand. The
 * admin console has consumed these since W4b.
 */
export function useTemplateRealtimeSync(): void {
  const qc = useQueryClient();

  const invalidate = (templateId?: string) => {
    qc.invalidateQueries({ queryKey: ['templates'] });
    if (templateId) qc.invalidateQueries({ queryKey: ['template', templateId] });
  };

  useTemplateEvent('template.approved', (e) => invalidate(e.template_id));
  useTemplateEvent('template.rejected', (e) => invalidate(e.template_id));
}
